import { logger } from '../../utils/logger';
import React, { useState, useEffect, useRef } from 'react';
import { TextField, Box, CircularProgress, Alert } from '@mui/material';
import { api } from '../../services/api';

interface AddressData {
  address_line1: string;
  address_line2?: string;
  city: string;
  state: string;
  zip_code: string;
  country: string;
}

interface GooglePlacesAutocompleteProxyProps {
  value: string;
  onChange: (address: string, addressData: AddressData) => void;
  label: string;
  required?: boolean;
  error?: boolean;
  helperText?: string;
  disabled?: boolean;
  placeholder?: string;
}

export const GooglePlacesAutocompleteProxy: React.FC<GooglePlacesAutocompleteProxyProps> = ({
  value,
  onChange,
  label,
  required = false,
  error = false,
  helperText,
  disabled = false,
  placeholder = 'Start typing an address...'
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [inputValue, setInputValue] = useState(value);
  const inputRef = useRef<HTMLInputElement>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [sessionToken, setSessionToken] = useState<string>('');
  const [suggestions, setSuggestions] = useState<any[]>([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const debounceTimerRef = useRef<NodeJS.Timeout>();

  // Update input value when prop changes
  useEffect(() => {
    setInputValue(value);
  }, [value]);

  // Generate session token for billing optimization
  const generateSessionToken = () => {
    return 'session_' + Math.random().toString(36).substr(2, 9) + Date.now().toString(36);
  };

  // Initialize session token
  useEffect(() => {
    setSessionToken(generateSessionToken());
  }, []);

  // Fetch autocomplete suggestions using backend proxy
  const fetchSuggestions = async (input: string) => {
    if (!input || input.length < 3) {
      setSuggestions([]);
      setShowDropdown(false);
      return;
    }

    try {
      setIsLoading(true);
      logger.log('Fetching suggestions via proxy for:', input);
      
      const response = await api.post('/api/v1/places/autocomplete', {
        input: input,
        sessionToken: sessionToken,
        includedRegionCodes: ['us', 'ca', 'mx', 'gb'],
        languageCode: 'en'
      });

      logger.log('Autocomplete proxy response:', response.data);
      
      if (response.data.suggestions && response.data.suggestions.length > 0) {
        setSuggestions(response.data.suggestions);
        setShowDropdown(true);
      } else {
        setSuggestions([]);
        setShowDropdown(false);
      }
    } catch (error: any) {
      logger.error('Error fetching suggestions via proxy:', error);
      if (error.response?.status === 500 && error.response?.data?.detail?.includes('API key')) {
        setLoadError('Google Places API is not properly configured on the server.');
      }
      setSuggestions([]);
      setShowDropdown(false);
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch place details when a suggestion is selected
  const fetchPlaceDetails = async (placeId: string) => {
    if (!placeId) return;

    try {
      setIsLoading(true);
      logger.log('Fetching place details via proxy for:', placeId);
      
      const response = await api.get(`/api/v1/places/details/${placeId}`, {
        params: {
          session_token: sessionToken
        }
      });

      const placeDetails = response.data;
      logger.log('Place details via proxy:', placeDetails);
      
      // Parse address components
      const addressData: AddressData = {
        address_line1: '',
        address_line2: '',
        city: '',
        state: '',
        zip_code: '',
        country: ''
      };
      
      if (placeDetails.addressComponents) {
        placeDetails.addressComponents.forEach((component: any) => {
          const types = component.types || [];
          
          if (types.includes('street_number')) {
            addressData.address_line1 = component.longText;
          } else if (types.includes('route')) {
            addressData.address_line1 += (addressData.address_line1 ? ' ' : '') + component.longText;
          } else if (types.includes('subpremise')) {
            addressData.address_line2 = component.longText;
          } else if (types.includes('locality')) {
            addressData.city = component.longText;
          } else if (types.includes('administrative_area_level_1')) {
            addressData.state = component.shortText;
          } else if (types.includes('postal_code')) {
            addressData.zip_code = component.longText;
          } else if (types.includes('country')) {
            addressData.country = component.shortText;
          }
        });
      }
      
      logger.log('Parsed address data:', addressData);
      
      // Update the input value and call onChange
      setInputValue(placeDetails.formattedAddress || '');
      onChange(placeDetails.formattedAddress || '', addressData);
      
      // Reset for next session
      setSessionToken(generateSessionToken());
      setSuggestions([]);
      setShowDropdown(false);
    } catch (error) {
      logger.error('Error getting place details via proxy:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setInputValue(newValue);
    
    // If the user clears the input, notify the parent component
    if (!newValue) {
      onChange('', {
        address_line1: '',
        city: '',
        state: '',
        zip_code: '',
        country: ''
      });
      setSuggestions([]);
      setShowDropdown(false);
      return;
    }

    // Allow manual typing
    onChange(newValue, {
      address_line1: newValue,
      city: '',
      state: '',
      zip_code: '',
      country: ''
    });

    // Debounce the API call
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    
    debounceTimerRef.current = setTimeout(() => {
      fetchSuggestions(newValue);
    }, 300);
  };

  const handleSuggestionClick = (suggestion: any) => {
    const placeId = suggestion.placePrediction?.placeId;
    if (placeId) {
      fetchPlaceDetails(placeId);
    }
  };

  // Handle clicks outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (inputRef.current && !inputRef.current.contains(event.target as Node)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, []);

  // Cleanup debounce timer
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  if (loadError) {
    return (
      <Box>
        <Alert severity="warning" sx={{ mb: 2 }}>
          {loadError}. Address autocomplete is temporarily unavailable. Please enter your address manually.
        </Alert>
        <TextField
          fullWidth
          label={label}
          value={inputValue}
          onChange={handleInputChange}
          required={required}
          error={error}
          helperText={helperText || 'Enter address manually'}
          disabled={disabled}
          placeholder={placeholder}
        />
      </Box>
    );
  }

  return (
    <Box position="relative">
      <TextField
        inputRef={inputRef}
        fullWidth
        label={label}
        value={inputValue}
        onChange={handleInputChange}
        required={required}
        error={error}
        helperText={helperText || 'Start typing to see address suggestions (3+ characters)'}
        disabled={disabled}
        placeholder={placeholder}
        InputProps={{
          endAdornment: isLoading && (
            <CircularProgress size={20} />
          )
        }}
      />
      
      {showDropdown && suggestions.length > 0 && (
        <Box
          sx={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            backgroundColor: 'white',
            border: '1px solid #ccc',
            borderTop: 'none',
            borderRadius: '0 0 4px 4px',
            maxHeight: '200px',
            overflowY: 'auto',
            zIndex: 1000,
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
          }}
        >
          {suggestions.map((suggestion, index) => (
            <Box
              key={index}
              onClick={() => handleSuggestionClick(suggestion)}
              sx={{
                padding: '12px',
                cursor: 'pointer',
                borderBottom: '1px solid #eee',
                fontSize: '14px',
                backgroundColor: 'white',
                transition: 'background-color 0.2s',
                '&:hover': {
                  backgroundColor: '#f5f5f5'
                },
                '&:last-child': {
                  borderBottom: 'none'
                }
              }}
            >
              <Box sx={{ fontWeight: 500 }}>
                {suggestion.placePrediction?.structuredFormat?.mainText?.text || 
                 suggestion.placePrediction?.text?.text || 
                 'Unknown place'}
              </Box>
              {suggestion.placePrediction?.structuredFormat?.secondaryText?.text && (
                <Box sx={{ fontSize: '12px', color: '#666', marginTop: '2px' }}>
                  {suggestion.placePrediction.structuredFormat.secondaryText.text}
                </Box>
              )}
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
};